/**
 * Utilities for parsing raw Strapi API responses
 *
 * Strapi v4 returns data in this format:
 * - Single: { data: { id, attributes: {...} }, meta: {...} }
 * - List: { data: [{ id, attributes: {...} }], meta: { pagination: {...} } }
 *
 * These utilities flatten the response to:
 * - Single: { id, ...attributes }
 * - List: [{ id, ...attributes }]
 *
 * And extract meta/pagination info separately.
 *
 * With schema validation:
 * - Pass a Zod schema to validate response data
 * - Pass populate array to enforce populated fields exist
 * - Validation is strict by default (throws on errors)
 */
import { ResponseMetaSchema } from "../schemas/common.schemas";
import { validateResponse } from "../schemas/validation";
/**
 * Check if object is a Strapi data wrapper { data: ..., meta?: ..., timestamp?: ... }
 *
 * Accepts responses with:
 * - { data } - minimal wrapper
 * - { data, meta } - standard Strapi format
 * - { data, timestamp } - custom controllers with timestamp
 * - { data, meta, timestamp } - full format
 */
function isStrapiWrapper(obj) {
    if (!obj || typeof obj !== "object")
        return false;
    if (!("data" in obj))
        return false;
    // Check that all keys are expected wrapper keys (data, meta, timestamp)
    const allowedKeys = new Set(["data", "meta", "timestamp"]);
    const keys = Object.keys(obj);
    return keys.every((key) => allowedKeys.has(key));
}
/**
 * Check if object is a Strapi entity with attributes { id, attributes: {...} }
 */
function isStrapiEntity(obj) {
    return (obj !== null &&
        typeof obj === "object" &&
        "id" in obj &&
        "attributes" in obj &&
        typeof obj.attributes === "object");
}
/**
 * Flatten a single Strapi entity from { id, attributes: {...} } to { id, ...attributes }
 */
function flattenEntity(entity) {
    const { id, attributes } = entity;
    const flattened = { id, ...attributes };
    // Recursively flatten nested relations
    for (const [key, value] of Object.entries(flattened)) {
        if (isStrapiWrapper(value)) {
            // Nested relation with { data: ... } wrapper
            flattened[key] = parseData(value.data);
        }
        else if (isStrapiEntity(value)) {
            // Direct entity reference
            flattened[key] = flattenEntity(value);
        }
        else if (Array.isArray(value)) {
            // Array of entities or values
            flattened[key] = value.map((item) => (isStrapiEntity(item) ? flattenEntity(item) : item));
        }
    }
    return flattened;
}
/**
 * Parse Strapi data (handles both single entity and arrays)
 */
function parseData(data) {
    if (data === null || data === undefined) {
        return null;
    }
    if (Array.isArray(data)) {
        return data.map((item) => (isStrapiEntity(item) ? flattenEntity(item) : item));
    }
    if (isStrapiEntity(data)) {
        return flattenEntity(data);
    }
    // Already flat or primitive
    return data;
}
/**
 * Parse a Strapi API response and extract data and meta
 *
 * @param response - Raw response from Strapi API
 * @returns Object with parsed data and meta
 */
export function parseStrapiResponse(response) {
    // Check if it's a Strapi wrapper format
    if (isStrapiWrapper(response)) {
        const parsed = parseData(response.data);
        const meta = response.meta || {};
        return { data: parsed, meta };
    }
    // Already flat response (custom controller, etc.)
    return { data: response, meta: {} };
}
/**
 * Parse a Strapi list response with optional schema validation
 *
 * @param response - Raw response from Strapi API
 * @param options - Optional parsing options with schema validation
 * @returns Object with parsed and validated data array and meta (including pagination)
 *
 * @example
 * ```typescript
 * // Without validation (backwards compatible)
 * const result = parseStrapiListResponse<User>(response.data);
 *
 * // With validation
 * const result = parseStrapiListResponse<User>(response.data, {
 *   schema: UserSchema,
 *   populate: USER_POPULATE.LIST,
 *   context: "usersEndpoints.getUsers",
 * });
 * ```
 */
export function parseStrapiListResponse(response, options) {
    const { data, meta } = parseStrapiResponse(response);
    const dataArray = Array.isArray(data) ? data : [];
    // Validate meta
    const validatedMeta = ResponseMetaSchema.safeParse(meta);
    // Validate each item if schema provided
    let validatedData = dataArray;
    if (options?.schema && dataArray.length > 0) {
        const validationOptions = {
            populate: options.populate,
            strict: options.strict,
        };
        validatedData = dataArray.map((item, i) => validateResponse(options.schema, item, {
            ...validationOptions,
            context: `${options.context ?? "unknown"}[${i}]`,
        }));
    }
    return {
        data: validatedData,
        meta: validatedMeta.success ? validatedMeta.data : {},
    };
}
/**
 * Parse a Strapi single item response with optional schema validation
 *
 * @param response - Raw response from Strapi API
 * @param options - Optional parsing options with schema validation
 * @returns Object with parsed and validated data and meta
 *
 * @example
 * ```typescript
 * // Without validation (backwards compatible)
 * const result = parseStrapiItemResponse<User>(response.data);
 *
 * // With validation
 * const result = parseStrapiItemResponse<User>(response.data, {
 *   schema: UserSchema,
 *   populate: USER_POPULATE.FULL,
 *   context: "usersEndpoints.getMe",
 * });
 * ```
 */
export function parseStrapiItemResponse(response, options) {
    const { data, meta } = parseStrapiResponse(response);
    // Validate meta
    const validatedMeta = ResponseMetaSchema.safeParse(meta);
    // Validate data if schema provided
    let validatedData = data;
    if (options?.schema && data !== null && data !== undefined) {
        validatedData = validateResponse(options.schema, data, {
            context: options.context,
            populate: options.populate,
            strict: options.strict,
        });
    }
    return {
        data: validatedData,
        meta: validatedMeta.success ? validatedMeta.data : {},
    };
}
